import update from "immutability-helper"
import axios from "axios"
import {ActionType, getType} from "typesafe-actions"
import _ from "lodash"
import {
    errorAction,
    loadAssaySuccessAction,
    loadClassesSuccessAction,
    loadConfigurationSuccessAction,
    loadDateTableDataSuccessAction,
    loadDetectabilitySummaryDataSuccessAction,
    loadMaterialsSuccessAction,
    loadMatricesSuccessAction,
    loadMetabolitesSuccessAction,
    loadProjectsSuccessAction,
    loadReferenceRangesDataSuccessAction,
    loadSpeciesAction,
    loadSpeciesSuccessAction,
    loadVariablesSuccessAction,
    RefdbActions,
    setDisplayDataContextAction,
    setMultipleContextAction,
    setGenerateTableType,
    loadValuesSuccessAction,
    loadReferenceRangesDataAction,
    dataClean,
    loadDateTableDataAction,
    loadDetectabilitySummaryDataAction,
    removeFromMultipleContext,
    cancelReferenceRangesDataSuccessAction,
    loadReferenceRangesInternalDataAction,
    loadExistingVariablesSuccessAction,
    exportDisplayDataAction,
    exportDetectabilitySummaryAction,
    exportDisplayDataSuccessAction,
    loadTotalNumberOfSamplesSuccessAction,
    loadDemographicsDataSuccessAction,
    loadDemographicsDataAction,
    setTableColumn,
} from "./refdb.actions"

import {initialState, State} from "./refdb.state"
import {showToast} from "../../common/toaster/AppToasterWrapper"
import {TableSorting} from "../../@biocrates-common-repo/core/models/tableSorting"
import TableColumn from "../../@biocrates-common-repo/core/models/tableColumn"
import env from "../../context/Env"

// TODO: could be refactored based on https://github.com/piotrwitek/typesafe-actions/blob/master/codesandbox/src/features/todos/reducer.ts
function compareStringIgnoreCase(valueA, valueB, nodeA, nodeB, isInverted) {
    if (valueA.toLowerCase() === valueB.toLowerCase()) return 0
    return valueA.toLowerCase() > valueB.toLowerCase() ? 1 : -1
}

const collator = new Intl.Collator("en", {
    numeric: true,
    sensitivity: "base",
})

function compareStringNaturalSorting(valueA, valueB, nodeA, nodeB, isInverted) {
    return collator.compare(valueA, valueB)
}

function renderLinks(value) {
    return value
        ? `<a href='${env("HMDB_URL")}${value}' target="_blank">${value}</a>`
        : ""
}

function getColumns(state: State): TableColumn[] {
    const columns: TableColumn[] = [
        {
            id: "shortName",
            name: "Short Name",
            decimalSchema: false,
            comparator: compareStringNaturalSorting,
            renderValue: undefined,
        },
        {
            id: "analyte",
            name: "Metabolite",
            decimalSchema: false,
            comparator: compareStringIgnoreCase,
        },
        {
            id: "aClass",
            name: "Class",
            decimalSchema: false,
            comparator: compareStringIgnoreCase,
        },
        {
            id: "median",
            name: "Median [µM]",
            decimalSchema: true,
        },
        {
            id: "avg",
            name: "Mean [µM]",
            decimalSchema: true,
        },
        {
            id: "min",
            name: "Min [µM]",
            decimalSchema: true,
        },
        {
            id: "max",
            name: "Max [µM]",
            decimalSchema: true,
        },
        {
            id: "q1",
            name: "1st quartile [µM]",
            decimalSchema: true,
        },
        {
            id: "q3",
            name: "3rd quartile [µM]",
            decimalSchema: true,
        },
        {
            id: "interQuartileRange",
            name: "Interquartal range [µM]",
            decimalSchema: true,
        },
        {
            id: "stddev",
            name: "Standard deviation",
            decimalSchema: true,
        },
        {
            id: "lod",
            name: "% of samples > LOD",
            decimalSchema: true,
        },
        {
            id: "typicalLOD",
            name: "Typical LOD [µM]",
            decimalSchema: true,
        },
        {
            id: "count",
            name: "No of samples",
            decimalSchema: false,
            renderValue: undefined,
        },
        {
            id: "hmdbId",
            name: "HMDB ID",
            decimalSchema: false,
            renderValue: renderLinks,
            hide: !state.context?.showHmdbId,
        },
    ]

    return columns
}

function getSorting(): TableSorting[] {
    return [
        {columnId: "analyte", order: "asc"},
        {columnId: "aClass", order: "asc"},
    ]
}

function getColumnsForDataTable(payload: any[]): TableColumn[] {
    const columnsForDataTable: TableColumn[] = [
        {
            id: "sample",
            name: "sample",
        },
        {
            id: "identification",
            name: "identification",
        },
        {
            id: "species",
            name: "species",
        },
        {
            id: "material",
            name: "material",
        },
        {
            id: "project",
            name: "project",
        },
    ]
    if (payload) {
        // const uniques = _.uniq(_.map(array, 'age'));
        const columns = payload
            .flatMap((entry) => Object.entries(entry.values))
            .map(([key, value]) => {
                return {
                    analyte: key,
                    class: (value as any).class,
                    unit: (value as any).unit,
                }
            })
        const uniqColumns = Array.from(
            new Map(columns.map((item) => [item.analyte, item])).values()
        )

        uniqColumns
            .sort(
                (a, b) =>
                    collator.compare(a.class, b.class) ||
                    collator.compare(a.analyte, b.analyte)
            )
            .forEach((item) =>
                columnsForDataTable.push({
                    id: item.analyte,
                    name: `${item.analyte} [${item.unit}]`,
                    default: "",
                })
            )
    }

    return columnsForDataTable
}

function getBaseColumnsForDetectabilitySummary(): TableColumn[] {
    return [
        {
            id: "analyte",
            name: "Metabolite",
            comparator: compareStringNaturalSorting,
        },
        {
            id: "aClass",
            name: "Class",
            comparator: compareStringNaturalSorting,
        },
    ]
}

function valueOfAnalyte(map, analyte) {
    return map[analyte] ? map[analyte].lod : 0
}

function toObject(columns: TableColumn[]): any {
    const emptyRow = {}
    columns.forEach((column) => {
        emptyRow[column.id] = 0
    })
    return emptyRow
}

function mergeArrayObjects(arr1, arr2, columnName, columns: TableColumn[]) {
    const map1 = _.keyBy(arr1 as any[], "analyte")
    const map2 = _.keyBy(arr2 as any[], "analyte")
    return _.uniq([...arr1, ...arr2].map((val) => val.analyte)).map(
        (analyte) => {
            if (map1[analyte] !== undefined) {
                return {
                    ...map1[analyte],
                    [columnName]: valueOfAnalyte(map2, analyte),
                }
            }
            return {
                ...toObject(columns),
                analyte,
                aClass: map2[analyte].aClass,
                [columnName]: valueOfAnalyte(map2, analyte),
            }
        }
    )
}

export default function (
    state: State = initialState,
    action: RefdbActions | ActionType<typeof errorAction>
): State {
    switch (action.type) {
        case getType(loadReferenceRangesDataAction):
        case getType(loadReferenceRangesInternalDataAction):
        case getType(loadDateTableDataAction):
        case getType(loadDetectabilitySummaryDataAction):
        case getType(loadDemographicsDataAction):
        case getType(loadSpeciesAction): {
            return update(state, {
                isLoadingDisplayData: {$set: true},
            })
        }
        case getType(exportDisplayDataAction):
        case getType(exportDetectabilitySummaryAction): {
            return update(state, {
                isExporting: {$set: true},
            })
        }
        case getType(exportDisplayDataSuccessAction): {
            return update(state, {
                isExporting: {$set: false},
            })
        }
        case getType(loadSpeciesSuccessAction): {
            return update(state, {
                displayData: {
                    contextValues: {
                        species: {$set: action.payload},
                    },
                },
                isLoadingDisplayData: {$set: false},
            })
        }

        case getType(loadMaterialsSuccessAction): {
            return update(state, {
                displayData: {
                    contextValues: {
                        materials: {$set: action.payload},
                    },
                },
                isLoadingDisplayData: {$set: false},
            })
        }
        case getType(loadAssaySuccessAction): {
            return update(state, {
                displayData: {
                    contextValues: {
                        assay: {$set: action.payload},
                    },
                },
                isLoadingDisplayData: {$set: false},
            })
        }
        case getType(loadMetabolitesSuccessAction): {
            return update(state, {
                displayData: {
                    contextValues: {
                        metabolites: {$set: action.payload},
                    },
                },
                isLoadingDisplayData: {$set: false},
            })
        }
        case getType(loadVariablesSuccessAction): {
            return update(state, {
                displayData: {
                    contextValues: {
                        variables: {$set: action.payload},
                    },
                },
            })
        }
        case getType(loadExistingVariablesSuccessAction): {
            return update(state, {
                displayData: {
                    contextValues: {
                        existingVariables: {
                            $set: _.mapValues(
                                _.groupBy(action.payload, "variable"),
                                (clist) => clist.map((item: any) => item.value)
                            ),
                        },
                    },
                },
            })
        }
        case getType(loadValuesSuccessAction): {
            return update(state, {
                displayData: {
                    contextValues: {
                        values: {
                            [action.payload.params]: {
                                $set: action.payload.result,
                            },
                        },
                    },
                },
            })
        }

        case getType(loadClassesSuccessAction): {
            return update(state, {
                displayData: {
                    contextValues: {
                        classes: {$set: action.payload},
                    },
                },
            })
        }
        case getType(loadProjectsSuccessAction): {
            return update(state, {
                displayData: {
                    contextValues: {
                        projects: {$set: action.payload},
                    },
                },
            })
        }

        case getType(loadMatricesSuccessAction): {
            return update(state, {
                displayData: {
                    contextValues: {
                        matrices: {$set: action.payload},
                    },
                },
            })
        }
        case getType(loadConfigurationSuccessAction): {
            return update(state, {
                displayData: {
                    contextValues: {
                        [action.payload.type]: {$set: action.payload.data},
                    },
                },
            })
        }

        case getType(loadReferenceRangesDataSuccessAction): {
            return update(state, {
                displayData: {
                    data: {
                        rows: {$set: action.payload},
                        analyteColumns: {$set: getColumns(state)},
                    },
                },
                isLoadingDisplayData: {$set: false},
            })
        }
        case getType(loadDemographicsDataSuccessAction): {
            return update(state, {
                displayData: {
                    data: {
                        demographics: {$set: action.payload},
                    },
                },
                isLoadingDisplayData: {$set: false},
            })
        }
        case getType(cancelReferenceRangesDataSuccessAction): {
            return update(state, {
                isLoadingDisplayData: {$set: false},
            })
        }
        case getType(loadDateTableDataSuccessAction): {
            return update(state, {
                displayData: {
                    data: {
                        rows: {$set: action.payload},
                        analyteColumns: {
                            $set: getColumnsForDataTable(action.payload),
                        },
                    },
                },
                isLoadingDisplayData: {$set: false},
            })
        }
        case getType(dataClean): {
            return update(state, {
                generateTable: {$set: undefined},
                displayData: {
                    data: {
                        rows: {$set: []},
                        analyteColumns: {
                            $set: [],
                        },
                        demographics: {$set: {}},
                    },
                },
                isLoadingDisplayData: {$set: false},
            })
        }
        case getType(loadTotalNumberOfSamplesSuccessAction): {
            return update(state, {
                context: {
                    totalSampleNumber: {$set: action.payload},
                },
            })
        }
        case getType(loadDetectabilitySummaryDataSuccessAction): {
            const columns = getBaseColumnsForDetectabilitySummary().concat(
                state.multiContext.map((context) => {
                    const identification = `${context.species}-${context.material}`
                    const label = `${context.species} ${context.material}`
                    return {
                        id: identification,
                        name: label,
                        decimalSchema: true,
                    }
                })
            )

            let merged

            const identification = `${
                state.multiContext[action.payload.action.payload].species
            }-${state.multiContext[action.payload.action.payload].material}`

            if (
                state.displayData.data.rows &&
                state.displayData.data.rows.length > 0
            ) {
                merged = mergeArrayObjects(
                    state.displayData.data.rows,
                    action.payload.response,
                    identification,
                    columns
                )
            }

            return update(state, {
                displayData: {
                    data: {
                        rows: {
                            $set:
                                merged ||
                                action.payload.response
                                    .map((value) => {
                                        return {
                                            ...value,
                                            [identification]: value.lod,
                                        }
                                    })
                                    .sort(
                                        (a, b) =>
                                            collator.compare(
                                                a.aClass,
                                                b.aClass
                                            ) ||
                                            collator.compare(
                                                a.analyte,
                                                b.analyte
                                            )
                                    ),
                        },
                        analyteColumns: {$set: columns},
                    },
                },
                isLoadingDisplayData: {$set: false},
            })
        }
        case getType(setTableColumn): {
            return update(state, {
                displayData: {
                    data: {
                        analyteColumns: {$set: action.payload},
                    },
                },
                isLoadingDisplayData: {$set: false},
            })
        }
        case getType(setDisplayDataContextAction): {
            return update(state, {context: {$set: action.payload}})
        }

        case getType(setMultipleContextAction): {
            const list = _.cloneDeep(state.multiContext)
            list[action.payload.index] = action.payload
            return update(state, {multiContext: {$set: list}})
        }
        case getType(removeFromMultipleContext): {
            const list = _.cloneDeep(state.multiContext)
            list.splice(action.payload, 1)
            return update(state, {multiContext: {$set: list}})
        }
        case getType(setGenerateTableType): {
            return update(state, {generateTable: {$set: action.payload}})
        }

        case getType(errorAction):
            console.error(action.payload)
            showToast({
                type: axios.isCancel(action.payload) ? "info" : "error",
                message: action.payload.message,
            })
            return state
        default:
            return state
    }
}
